<script setup>
// Modal 911 al RESPONSABLE (Victor 2026-05-25, propuesta P2 "Tarjeta ejecutiva").
// Al iniciar sesión, si el usuario es responsable de clientes en atraso
// (Retoque/Impresión/Álbum), le muestra la lista una vez por día, personalizada
// con su nombre. Se auto-gatea: sin atrasos no aparece. No se le muestra a la
// encargada de Selección (esa ya recibe el modal de TODA la sucursal).
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import DealsAPI from 'dashboard/api/deals';

const store = useStore();
const router = useRouter();

const visible = ref(false);
const items = ref([]);
let ran = false;

const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const currentAccount = computed(() => store.getters.getCurrentAccount || null);
const currentUser = computed(() => store.getters.getCurrentUser || {});
const fullName = computed(
  () => currentUser.value?.name || currentUser.value?.available_name || ''
);
// La encargada de Selección y los encargados generales ya ven el modal de TODA
// la sucursal → no duplicar el aviso del responsable para ellos.
const isSeleccion = computed(
  () => currentAccount.value?.feature_permissions?.seleccion_retoque === true
);
const BRANCH_MANAGER_IDS = [41, 25, 7, 6, 4, 19, 20];
const currentUserId = computed(() => store.getters.getCurrentUserID);
const isBranchManager = computed(() =>
  BRANCH_MANAGER_IDS.includes(Number(currentUserId.value))
);

function seenKey() {
  const uid = store.getters.getCurrentUserID;
  const today = new Date().toISOString().slice(0, 10);
  return `responsable_atrasos_seen_${uid}_${today}`;
}

function deptShort(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('retoque')) return 'Retoque';
  if (/impresi[oó]n/.test(n)) return 'Impresión';
  if (/[aá]lbum/.test(n)) return 'Álbum';
  return name;
}

function initials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase())
    .join('');
}

async function maybeShow() {
  if (isSeleccion.value || isBranchManager.value) return;
  try {
    if (localStorage.getItem(seenKey())) return;
  } catch (e) {
    /* ignore */
  }
  try {
    const { data } = await DealsAPI.misAtrasados();
    items.value = data.payload || [];
    if (items.value.length) visible.value = true;
  } catch (e) {
    /* silencioso */
  }
}

function close() {
  try {
    localStorage.setItem(seenKey(), '1');
  } catch (e) {
    /* ignore */
  }
  visible.value = false;
}

// No obligatorio para el dueño y Gabriela (Victor 2026-06-11): Victor (1,3) y
// Gabriela Abreu (41) pueden cerrar el aviso con clic fuera de la tarjeta; para
// el resto sigue siendo obligatorio.
const DISMISSIBLE_USER_IDS = [1, 3, 41];
const canDismiss = computed(() =>
  DISMISSIBLE_USER_IDS.includes(Number(currentUserId.value))
);
function onBackdrop() {
  if (canDismiss.value) close();
}

function goTo(item) {
  close();
  router.push({
    name: 'pipelines_view',
    params: { accountId: currentAccountId.value, pipelineId: item.pipeline_id },
  });
}

watch(
  currentAccount,
  acc => {
    if (acc && acc.id && !ran) {
      ran = true;
      maybeShow();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="visible" class="ar-overlay" @click.self="onBackdrop">
    <div class="ar-card">
      <div class="ar-bar" />
      <div class="ar-in">
        <div class="ar-hdr">
          <div class="ar-seal">🚨</div>
          <div>
            <div class="ar-who">{{ fullName }}</div>
            <div class="ar-role">Responsable de estos clientes</div>
          </div>
        </div>

        <div class="ar-msg">
          Tienes <b>{{ items.length }}</b>
          {{ items.length === 1 ? 'cliente asignado' : 'clientes asignados' }} en atraso.
          Necesitamos que les des <b>seguimiento hasta resolverlos</b> — un cliente detenido afecta
          la <b>calidad de la empresa</b>. Comunícate con la encargada de cada departamento para
          <b>trabajarlos</b>, ya que necesitan <span class="ar-911">atención 911</span>.
        </div>

        <div class="ar-list">
          <button
            v-for="it in items"
            :key="it.id"
            type="button"
            class="ar-row"
            @click="goTo(it)"
          >
            <span class="ar-av">{{ initials(it.contact_name) }}</span>
            <span class="ar-c">
              <span class="ar-nm">{{ it.contact_name }}</span>
              <span class="ar-meta">
                {{ deptShort(it.pipeline_name) }} · {{ it.days_in_stage }} días
                <span v-if="it.encargada" class="ar-gd">→ comunícate con {{ it.encargada }}</span>
                <span v-else class="ar-gd">→ encargada de {{ deptShort(it.pipeline_name) }}</span>
              </span>
            </span>
          </button>
        </div>

        <button type="button" class="ar-btn" @click="close">Atender ahora →</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ar-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}
.ar-card {
  width: 100%;
  max-width: 440px;
  background: linear-gradient(180deg, #181519, #141417);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.7);
}
.ar-bar { height: 3px; background: linear-gradient(90deg, #c9a35a, rgba(201, 163, 90, 0.1)); }
.ar-in { padding: 18px 18px 16px; }
.ar-hdr {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.ar-seal {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex-shrink: 0;
  background: rgba(226, 87, 92, 0.12);
  border: 1px solid rgba(226, 87, 92, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.ar-who { font-size: 16px; font-weight: 800; color: #fff; }
.ar-role {
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 1px;
  color: #c9a35a;
  text-transform: uppercase;
  margin-top: 1px;
}
.ar-msg {
  font-size: 11.5px;
  color: #fde2e2;
  line-height: 1.5;
  padding: 11px 12px;
  margin-bottom: 12px;
  background: rgba(226, 87, 92, 0.07);
  border: 1px solid rgba(226, 87, 92, 0.18);
  border-radius: 10px;
}
.ar-msg b { color: #fff; }
.ar-911 { font-weight: 900; color: #e2575c; }
.ar-list { border-top: 1px solid rgba(255, 255, 255, 0.07); }
.ar-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 12px 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.045);
  background: transparent;
  border-left: none;
  border-right: none;
  border-top: none;
  cursor: pointer;
}
.ar-row:hover { background: rgba(255, 255, 255, 0.02); }
.ar-av {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.ar-c { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ar-nm {
  font-size: 12.5px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ar-meta {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ar-gd { color: #c9a35a; }
.ar-btn {
  margin-top: 14px;
  width: 100%;
  background: #c9a35a;
  color: #1a1407;
  border: none;
  font-weight: 800;
  font-size: 12px;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
}
.ar-btn:hover { background: #d8b572; }
</style>
